import React, { useEffect, useState } from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { getUnifiedWithdrawalHistory } from "../../../services/walletService";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  ArrowDownToLine,
  CheckCircle2,
  Clock,
  XCircle,
  Trophy,
  Users,
  HelpCircle,
} from "lucide-react";

const WithdrawalHistory = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const navigate = useNavigate();

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const data = await getUnifiedWithdrawalHistory(page);
      if (data?.result) {
        setHistory(data.result.withdrawalRecords || []);
        setTotalPages(data.result.pagination?.totalPagesCount || 1);
      }
    } catch (error) {
      console.error("Error fetching withdrawal history:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, [page]);

  const getStatusStyle = (status) => {
    switch (status) {
      case "success":
        return {
          bg: "bg-green-500/10",
          text: "text-green-400",
          icon: CheckCircle2,
        };
      case "pending":
        return {
          bg: "bg-yellow-500/10",
          text: "text-yellow-400",
          icon: Clock,
        };
      default:
        return {
          bg: "bg-red-500/10",
          text: "text-red-400",
          icon: XCircle,
        };
    }
  };

  return (
    <div className="min-h-screen bg-[#2A1B3D] pt-20 pb-20 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-full h-[500px] bg-gradient-to-b from-purple-900/20 to-transparent pointer-events-none"></div>
      <div className="absolute top-[-100px] right-[-100px] w-96 h-96 bg-purple-600/10 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-0 left-[-100px] w-80 h-80 bg-indigo-600/10 rounded-full blur-3xl pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Back Button */}
        <motion.button
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-400 hover:text-white text-sm font-medium mb-6 transition-colors group"
        >
          <ArrowLeft
            size={16}
            className="group-hover:-translate-x-0.5 transition-transform"
          />
          Back to Wallet
        </motion.button>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-2">
            <ArrowDownToLine size={28} className="text-purple-400" />
            <h1 className="text-3xl md:text-4xl font-bold text-white">
              Withdrawal History
            </h1>
          </div>
          <p className="text-gray-400 text-sm md:text-base">
            Track all your withdrawal requests and payouts
          </p>
        </motion.div>

        {/* Table Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="bg-[#3F234E] rounded-3xl border border-white/5 overflow-hidden"
        >
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="flex flex-col items-center gap-4">
                <div className="w-10 h-10 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
                <p className="text-gray-400 text-sm">
                  Loading history...
                </p>
              </div>
            </div>
          ) : history.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-500">
              <ArrowDownToLine size={48} className="mb-4 opacity-20" />
              <p className="text-base font-medium text-gray-400">
                No withdrawal history found
              </p>
              <p className="text-sm text-gray-600 mt-1">
                Your withdrawal requests will appear here
              </p>
            </div>
          ) : (
            <>
              {/* Mobile Card View */}
              <div className="block md:hidden p-4 space-y-3">
                {history.map((item) => {
                  const statusStyle = getStatusStyle(item.status);
                  const StatusIcon = statusStyle.icon;
                  return (
                    <div
                      key={item._id}
                      className="bg-[#2A1B3D]/50 border border-white/5 rounded-xl p-4"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-white text-lg font-bold">
                          {item.coinTobededuct || item.amount} credits
                        </span>
                        <span
                          className={`flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full ${statusStyle.bg} ${statusStyle.text}`}
                        >
                          <StatusIcon size={12} />
                          {item.status.toUpperCase()}
                        </span>
                      </div>

                      {/* Deduction Details */}
                      <div className="flex flex-wrap gap-2 mb-3">
                        {item.deductedFrom?.contestCoins > 0 && (
                          <span className="flex items-center gap-1 text-xs bg-orange-500/10 text-orange-400 px-2 py-0.5 rounded-full">
                            <Trophy size={10} />
                            Contest: {item.deductedFrom.contestCoins}
                          </span>
                        )}
                        {item.deductedFrom?.referralCoins > 0 && (
                          <span className="flex items-center gap-1 text-xs bg-blue-500/10 text-blue-400 px-2 py-0.5 rounded-full">
                            <Users size={10} />
                            Referral: {item.deductedFrom.referralCoins}
                          </span>
                        )}
                        {item.deductedFrom?.questionCoins > 0 && (
                          <span className="flex items-center gap-1 text-xs bg-green-500/10 text-green-400 px-2 py-0.5 rounded-full">
                            <HelpCircle size={10} />
                            Quiz: {item.deductedFrom.questionCoins}
                          </span>
                        )}
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-gray-600 text-xs">
                          {moment(item.createdOn).format("DD MMM YYYY, hh:mm A")}
                        </span>
                        {item.paymentDoneOn && (
                          <span className="text-gray-500 text-xs">
                            Paid: {moment(item.paymentDoneOn).format("DD MMM YYYY")}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/5">
                      <th className="text-left text-gray-500 text-xs font-bold uppercase tracking-wider py-4 px-6">
                        Date
                      </th>
                      <th className="text-right text-gray-500 text-xs font-bold uppercase tracking-wider py-4 px-6">
                        Amount
                      </th>
                      <th className="text-left text-gray-500 text-xs font-bold uppercase tracking-wider py-4 px-6">
                        Status
                      </th>
                      <th className="text-left text-gray-500 text-xs font-bold uppercase tracking-wider py-4 px-6">
                        Deduction Details
                      </th>
                      <th className="text-left text-gray-500 text-xs font-bold uppercase tracking-wider py-4 px-6">
                        Payment Date
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((item) => {
                      const statusStyle = getStatusStyle(item.status);
                      const StatusIcon = statusStyle.icon;
                      return (
                        <tr
                          key={item._id}
                          className="border-b border-white/5 hover:bg-white/[0.02] transition-colors"
                        >
                          <td className="py-4 px-6">
                            <p className="text-white text-sm font-medium">
                              {moment(item.createdOn).format("DD MMM YYYY")}
                            </p>
                            <p className="text-gray-600 text-xs mt-0.5">
                              {moment(item.createdOn).format("hh:mm A")}
                            </p>
                          </td>
                          <td className="py-4 px-6 text-right">
                            <span className="text-white text-sm font-bold">
                              {item.coinTobededuct || item.amount} credits
                            </span>
                          </td>
                          <td className="py-4 px-6">
                            <span
                              className={`inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full ${statusStyle.bg} ${statusStyle.text}`}
                            >
                              <StatusIcon size={12} />
                              {item.status.toUpperCase()}
                            </span>
                          </td>
                          <td className="py-4 px-6">
                            <div className="flex flex-wrap gap-1.5">
                              {item.deductedFrom?.contestCoins > 0 && (
                                <span className="flex items-center gap-1 text-xs bg-orange-500/10 text-orange-400 px-2 py-0.5 rounded-full">
                                  <Trophy size={10} />
                                  Contest: {item.deductedFrom.contestCoins}
                                </span>
                              )}
                              {item.deductedFrom?.referralCoins > 0 && (
                                <span className="flex items-center gap-1 text-xs bg-blue-500/10 text-blue-400 px-2 py-0.5 rounded-full">
                                  <Users size={10} />
                                  Referral: {item.deductedFrom.referralCoins}
                                </span>
                              )}
                              {item.deductedFrom?.questionCoins > 0 && (
                                <span className="flex items-center gap-1 text-xs bg-green-500/10 text-green-400 px-2 py-0.5 rounded-full">
                                  <HelpCircle size={10} />
                                  Quiz: {item.deductedFrom.questionCoins}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="py-4 px-6 text-gray-400 text-sm">
                            {item.paymentDoneOn
                              ? moment(item.paymentDoneOn).format(
                                  "DD MMM YYYY"
                                )
                              : "-"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-4 p-4 border-t border-white/5">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-2 rounded-lg bg-[#2A1B3D] border border-white/10 text-gray-400 hover:text-white hover:border-purple-500/30 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <span className="text-gray-400 text-sm">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() =>
                      setPage((p) => Math.min(totalPages, p + 1))
                    }
                    disabled={page === totalPages}
                    className="p-2 rounded-lg bg-[#2A1B3D] border border-white/10 text-gray-400 hover:text-white hover:border-purple-500/30 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              )}
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default WithdrawalHistory;
